import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import type { QuickAmountSuggestion } from '../types';

interface AmountInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  minAmount: number;
  maxAmount: number;
  suggestions: QuickAmountSuggestion[];
}

const AmountInput = ({ value, onChange, error, disabled, minAmount, maxAmount, suggestions }: AmountInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const formatCurrency = (val: string) => {
    const numericValue = val.replace(/[^0-9.]/g, '');
    const parts = numericValue.split('.');
    
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    
    if (parts[1] && parts[1].length > 2) {
      return parts[0] + '.' + parts[1].slice(0, 2);
    }
    
    return numericValue;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCurrency(e.target.value);
    setDisplayValue(formatted);
    onChange(formatted);
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (displayValue) {
      const num = parseFloat(displayValue);
      if (!isNaN(num)) {
        const formatted = num.toFixed(2);
        setDisplayValue(formatted);
        onChange(formatted);
      }
    }
  };

  const handleSuggestionClick = (amount: number) => {
    const formatted = amount.toFixed(2);
    setDisplayValue(formatted);
    onChange(formatted);
  };

  const numericValue = parseFloat(displayValue) || 0;
  const isValid = numericValue >= minAmount && numericValue <= maxAmount;

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        Contribution Amount <span className="text-destructive">*</span>
      </label>

      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
          VT
        </div>
        <input
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder="0.00"
          className={`
            w-full pl-12 pr-4 py-3 text-lg font-medium rounded-md border
            transition-interactive
            ${error ? 'border-destructive' : isFocused ? 'border-ring ring-2 ring-ring/20' : 'border-input'}
            ${disabled ? 'bg-muted cursor-not-allowed opacity-60' : 'bg-card'}
          `}
        />
        {displayValue && isValid && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <Icon name="CheckCircle" size={20} color="var(--color-success)" />
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-destructive flex items-center gap-1">
          <Icon name="AlertCircle" size={14} />
          {error}
        </p>
      )}

      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Icon name="Info" size={14} />
        <span>Min: {minAmount.toFixed(2)} VT • Max: {maxAmount.toFixed(2)} VT</span>
      </div>

      {suggestions.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-medium text-muted-foreground mb-2">Quick Amounts</div>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion.amount)}
                disabled={disabled}
                className={`
                  px-3 py-2 rounded-md border text-sm font-medium
                  transition-interactive hover-scale
                  ${displayValue === suggestion.amount.toFixed(2)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-foreground border-border hover:border-primary'
                  }
                  ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
                `}
              >
                {suggestion.amount.toFixed(2)} VT
                {suggestion.frequency > 0 && (
                  <span className="ml-1 text-xs opacity-70">({suggestion.frequency}x)</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AmountInput;